import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useNeighborhoodBySlug,
  useNeighborhoodAmenities,
  useNeighborhoodGuideSections,
  useNeighborhoodStats,
} from '@/hooks/useNeighborhoods';
import { useLocalize } from '@/hooks/useLandingContent';
import { searchApi } from '@/services/api';
import { HeroGallery } from '@/components/area-guides/HeroGallery';
import { HighlightsOverviewCard } from '@/components/area-guides/HighlightsOverviewCard';
import { ExploreAmenities } from '@/components/area-guides/ExploreAmenities';
import { PropertyScrollRow } from '@/components/landing/PropertyScrollRow';

export default function AreaGuideDetail() {
  const { citySlug: cs, neighborhoodSlug: ns } = useParams();
  const { data: neighborhood, isLoading } = useNeighborhoodBySlug(cs, ns);
  const { data: amenities = [] } = useNeighborhoodAmenities(neighborhood?.id);
  const { data: sections = [] } = useNeighborhoodGuideSections(neighborhood?.id);
  const { data: stats } = useNeighborhoodStats(neighborhood?.id);
  const localize = useLocalize();
  const [showMore, setShowMore] = useState(false);
  const [listingType, setListingType] = useState<'sale' | 'rent'>('sale');
  const [propertyType, setPropertyType] = useState<string>('all');
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');

  const cityName = neighborhood?.city?.name as string | undefined;
  const subCityName = neighborhood?.name as string | undefined;

  const { data: listings = [], isFetching: listingsLoading } = useQuery({
    queryKey: ['ag-listings', cityName, subCityName, listingType, propertyType, minPrice, maxPrice],
    enabled: !!cityName && !!subCityName,
    queryFn: async () => {
      const params: any = {
        city: cityName!,
        sub_city: subCityName!,
        listing_type: listingType,
        limit: '12',
      };
      if (propertyType !== 'all') params.property_type = propertyType;
      if (minPrice) params.min_price = minPrice;
      if (maxPrice) params.max_price = maxPrice;
      const r = await searchApi.search(params);
      return r.properties || [];
    },
  });

  if (isLoading) {
    return <Layout><div className="container py-12">Loading…</div></Layout>;
  }
  if (!neighborhood || !cityName) {
    return <Layout><div className="container py-12">Area guide not found.</div></Layout>;
  }

  const description = localize(neighborhood.description) || neighborhood.tagline || '';
  const gallery = neighborhood.gallery?.length ? neighborhood.gallery : (neighborhood.cover_image_url ? [neighborhood.cover_image_url] : []);

  const cityQ = encodeURIComponent(cityName);
  const subQ = encodeURIComponent(subCityName!);

  return (
    <Layout>
      <SEOHead
        title={`${neighborhood.name} ${cityName}: Location, Amenities and Plots`}
        description={neighborhood.tagline || `Explore ${neighborhood.name} in ${cityName}. Property prices, amenities, schools and listings.`}
        canonical={`/area-guides/${cs}/${ns}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Place',
          name: `${neighborhood.name}, ${cityName}`,
          description,
          geo: neighborhood.latitude && neighborhood.longitude ? {
            '@type': 'GeoCoordinates',
            latitude: neighborhood.latitude,
            longitude: neighborhood.longitude,
          } : undefined,
          image: neighborhood.cover_image_url || undefined,
        }}
      />
      <div className="container py-6 space-y-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap">
          <Link to="/" className="hover:text-primary">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/area-guides" className="hover:text-primary">Area Guides</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/area-guides?city=${cityQ}`} className="hover:text-primary">{cityName}</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium">{neighborhood.name}</span>
        </nav>

        <h1 className="text-2xl md:text-3xl font-bold">
          {neighborhood.name} {cityName}: Location, Amenities and Plots
        </h1>

        {/* Hero + sidebar */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            <HeroGallery images={gallery} alt={neighborhood.name} />
            {neighborhood.society_map_image_url && (
              <div className="grid grid-cols-2 gap-3">
                <div className="relative aspect-video rounded-lg overflow-hidden border">
                  <img src={neighborhood.society_map_image_url} alt="Society Map" className="h-full w-full object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-white text-xs font-medium">Society Map</div>
                </div>
                <div className="relative aspect-video rounded-lg overflow-hidden border">
                  <img src={gallery[gallery.length - 1]} alt="All Photos" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-sm font-semibold">+{gallery.length} More</div>
                </div>
              </div>
            )}
          </div>
          <div>
            <HighlightsOverviewCard stats={stats as any} cityQuery={cityQ} subCityQuery={subQ} />
          </div>
        </div>

        {/* Area guide TOC + sections */}
        {sections.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold">{neighborhood.name} Area Guide</h2>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm font-semibold mb-2">Table of Contents</div>
              <ol className="space-y-1.5">
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#sec-${s.id}`} className="text-sm text-primary hover:underline">
                      {i + 1}- {localize(s.heading)}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
            <div className="space-y-6">
              {sections.map((s, i) => {
                const body = localize(s.body);
                const isFirst = i === 0;
                const truncated = isFirst && !showMore && body.length > 320 ? body.slice(0, 320) + '…' : body;
                return (
                  <article key={s.id} id={`sec-${s.id}`} className="space-y-2">
                    <h3 className="text-base md:text-lg font-semibold">{localize(s.heading)}</h3>
                    <p className="text-sm md:text-base text-muted-foreground whitespace-pre-line">{truncated}</p>
                    {isFirst && body.length > 320 && (
                      <button onClick={() => setShowMore(!showMore)} className="text-sm text-primary hover:underline">
                        {showMore ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {/* Society map full block */}
        {neighborhood.society_map_image_url && (
          <section className="space-y-3">
            <h2 className="text-xl md:text-2xl font-bold">{neighborhood.name} Society Map</h2>
            <div className="rounded-lg overflow-hidden border">
              <img src={neighborhood.society_map_image_url} alt={`${neighborhood.name} society map`} className="w-full h-auto" />
            </div>
          </section>
        )}

        {/* Explore amenities */}
        <ExploreAmenities
          amenities={amenities as any}
          centerLat={neighborhood.latitude}
          centerLng={neighborhood.longitude}
          neighborhoodName={neighborhood.name}
        />

        {/* Listings with filters */}
        <section className="space-y-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <h2 className="text-xl md:text-2xl font-bold">Properties in {neighborhood.name}</h2>
            <Tabs value={listingType} onValueChange={(v) => setListingType(v as 'sale' | 'rent')}>
              <TabsList>
                <TabsTrigger value="sale">For Sale</TabsTrigger>
                <TabsTrigger value="rent">For Rent</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Select value={propertyType} onValueChange={setPropertyType}>
              <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Property type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {['villa','apartment','flat','penthouse','room','commercial','land'].map(t => (
                  <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="number" placeholder="Min price" className="h-9 text-xs" value={minPrice} onChange={e => setMinPrice(e.target.value)} />
            <Input type="number" placeholder="Max price" className="h-9 text-xs" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} />
            <Button variant="outline" className="h-9 text-xs" onClick={() => { setPropertyType('all'); setMinPrice(''); setMaxPrice(''); }}>Clear</Button>
          </div>
          {listingsLoading ? (
            <div className="text-sm text-muted-foreground py-6">Loading…</div>
          ) : listings.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6">No properties match these filters.</div>
          ) : (
            <PropertyScrollRow title="" properties={listings} />
          )}
        </section>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button asChild>
            <Link to={`/buy?city=${cityQ}&subCity=${subQ}`}>View all houses for sale</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={`/rent?city=${cityQ}&subCity=${subQ}`}>View houses for rent</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={`/plots?city=${cityQ}&subCity=${subQ}`}>View plots</Link>
          </Button>
        </div>
      </div>
    </Layout>
  );
}
